/**
 * Task #245 — Pure ranking + merging logic for the Spotlight quick search.
 *
 * The hook feeds local (Tier 1) and server (Tier 2/3/4) hits in via
 * `mergeRankedHits`. Pure function, unit-tested in isolation.
 */

import type { SearchHit } from './types';

/**
 * Merge multiple tier outputs into one ranked list. Dedupes by projectId
 * — the first occurrence wins (callers should pass tiers in order of
 * trustworthiness: local fuzzy first, then server quick, then deep).
 *
 * Sort is by descending score, but identical-score rows keep their
 * original insertion order so the local result list does not jitter as
 * server hits arrive.
 */
export function mergeRankedHits(...tiers: SearchHit[][]): SearchHit[] {
  const seen = new Set<string>();
  const merged: SearchHit[] = [];
  for (const tier of tiers) {
    for (const hit of tier) {
      if (seen.has(hit.projectId)) continue;
      seen.add(hit.projectId);
      merged.push(hit);
    }
  }
  // Stable sort: index tag preserves insertion order for ties.
  const indexed = merged.map((hit, i) => ({ hit, i }));
  indexed.sort((a, b) => {
    if (b.hit.score !== a.hit.score) return b.hit.score - a.hit.score;
    return a.i - b.i;
  });
  return indexed.map(x => x.hit);
}

/**
 * Truncate a long project name to keep the row single-line. When the
 * matched fragment lives in the truncated tail, reorder the visible
 * slice so the highlight is always visible (acceptance #14).
 *
 * Returns the visible string plus adjusted `matchIndices` pointing into
 * the truncated representation.
 */
export function visibleSlice(
  value: string,
  matchIndices: Array<[number, number]>,
  maxLen: number = 60,
): { text: string; indices: Array<[number, number]> } {
  if (value.length <= maxLen) {
    return { text: value, indices: matchIndices };
  }
  const firstMatch = matchIndices[0];
  if (!firstMatch || firstMatch[0] < maxLen) {
    return {
      text: value.slice(0, maxLen) + '…',
      indices: matchIndices.filter(([s]) => s < maxLen).map(([s, e]) => [s, Math.min(e, maxLen - 1)] as [number, number]),
    };
  }
  // Match in the tail — surface the match window prefixed by an ellipsis
  // plus a small head fragment so the row still anchors visually.
  const matchLen = firstMatch[1] - firstMatch[0] + 1;
  const tailBudget = Math.min(maxLen - 6, matchLen + 16);
  const tailStart = Math.max(0, firstMatch[0] - 4);
  const tailEnd = Math.min(value.length, tailStart + tailBudget);
  const head = value.slice(0, 4);
  const tail = value.slice(tailStart, tailEnd);
  const text = `${head}…${tail}${tailEnd < value.length ? '…' : ''}`;
  // Recompute match indices relative to the produced text.
  const prefix = head.length + 1; // '…'
  const newStart = prefix + (firstMatch[0] - tailStart);
  const newEnd = newStart + matchLen - 1;
  return { text, indices: [[newStart, newEnd]] };
}
